
import { useEffect, useState } from 'react';
import { latLongToVector3 } from '../utils/globeUtils';

export interface User {
  id: string;
  ip: string;
  name?: string;
  isCurrentUser: boolean;
  position: [number, number, number];
  lat?: number;
  long?: number;
}

export const useUsers = (size: number, userMarkerPosition: [number, number, number] | null, ipAddress: string) => {
  const [users, setUsers] = useState<User[]>([]);
  
  // Add current user when location is detected
  useEffect(() => {
    if (userMarkerPosition) {
      try {
        // Extract lat/long from position for consistency
        // This is an approximation, better to store the original lat/long
        const lat = Math.asin(userMarkerPosition[1] / size) * (180 / Math.PI);
        const long = Math.atan2(userMarkerPosition[2], userMarkerPosition[0]) * (180 / Math.PI);
        
        setUsers(prev => {
          const currentUserExists = prev.some(u => u.id === "current-user");
          if (currentUserExists) {
            return prev.map(user => 
              user.id === "current-user" 
                ? { 
                    ...user, 
                    ip: ipAddress, 
                    position: userMarkerPosition,
                    lat,
                    long
                  }
                : user
            );
          } else {
            return [
              ...prev,
              {
                id: "current-user",
                ip: ipAddress,
                isCurrentUser: true,
                position: userMarkerPosition,
                lat,
                long
              }
            ];
          }
        });
      } catch (error) {
        console.error("Error calculating user position:", error);
        // Add user without lat/long if calculation fails
        setUsers(prev => {
          const currentUserExists = prev.some(u => u.id === "current-user");
          if (!currentUserExists) {
            return [
              ...prev,
              {
                id: "current-user",
                ip: ipAddress,
                isCurrentUser: true,
                position: userMarkerPosition
              }
            ];
          }
          return prev;
        });
      }
    }
  }, [userMarkerPosition, ipAddress, size]);
  
  // Add realistic mock users based on major cities
  useEffect(() => {
    const mockUsers = [
      { lat: 40.7128, long: -74.0060, ip: "192.168.1.1", name: "User New York" }, // New York
      { lat: 34.0522, long: -118.2437, ip: "192.168.1.2", name: "User Los Angeles" }, // Los Angeles
      { lat: 51.5074, long: -0.1278, ip: "192.168.1.3", name: "User London" }, // London
      { lat: 35.6762, long: 139.6503, ip: "192.168.1.4", name: "User Tokyo" }, // Tokyo
      { lat: -33.8688, long: 151.2093, ip: "192.168.1.5", name: "User Sydney" }, // Sydney
      { lat: 55.7558, long: 37.6173, ip: "192.168.1.6", name: "User Moscow" }, // Moscow
      { lat: -22.9068, long: -43.1729, ip: "192.168.1.7", name: "User Rio" }, // Rio de Janeiro
      { lat: 28.6139, long: 77.2090, ip: "192.168.1.8", name: "User New Delhi" }, // New Delhi
      { lat: 1.3521, long: 103.8198, ip: "192.168.1.9", name: "User Singapore" }, // Singapore
      { lat: -26.2041, long: 28.0473, ip: "192.168.1.10", name: "User Johannesburg" }, // Johannesburg
      { lat: 19.4326, long: -99.1332, ip: "192.168.1.11", name: "User Mexico City" }, // Mexico City
      { lat: 37.7749, long: -122.4194, ip: "192.168.1.12", name: "User San Francisco" }, // San Francisco
      { lat: 52.5200, long: 13.4050, ip: "192.168.1.13", name: "User Berlin" }, // Berlin
      { lat: 48.8566, long: 2.3522, ip: "192.168.1.14", name: "User Paris" }, // Paris
      { lat: 41.9028, long: 12.4964, ip: "192.168.1.15", name: "User Rome" }, // Rome
      { lat: 43.8563, long: 18.4131, ip: "192.168.1.16", name: "User Sarajevo" }, // Sarajevo
      { lat: 45.8150, long: 15.9819, ip: "192.168.1.17", name: "User Zagreb" }, // Zagreb
      { lat: 44.7866, long: 20.4489, ip: "192.168.1.18", name: "User Belgrade" }, // Belgrade
      { lat: 44.4268, long: 26.1025, ip: "192.168.1.19", name: "User Bucharest" }, // Bucharest
      { lat: 42.6977, long: 23.3219, ip: "192.168.1.20", name: "User Sofia" } // Sofia
    ];
    
    try {
      const additionalUsers = mockUsers.map((user, index) => {
        // Ensure user has both lat and long before calculating position
        if (user.lat !== undefined && user.long !== undefined) {
          const position = latLongToVector3(user.lat, user.long, size * 1.05);
          return {
            id: `user-${index}`,
            ip: user.ip,
            name: user.name,
            isCurrentUser: false,
            position,
            lat: user.lat,
            long: user.long
          };
        }
        return null;
      }).filter(Boolean) as User[];
      
      // Add mock users to state
      setUsers(prevUsers => {
        // Filter out any existing mock users
        const currentUser = prevUsers.find(u => u.isCurrentUser);
        return currentUser ? [...additionalUsers, currentUser] : additionalUsers;
      });
    } catch (error) {
      console.error("Error creating mock users:", error);
    }
  }, [size]);
  
  return users;
};
